import type {
  T_AssistantContextData,
  T_AssistantLimit,
  T_AssistantOffer,
  T_AssistantOfferKind,
  T_AssistantOfferVariables,
  T_AssistantTurn,
  T_AssistantTurnMeta,
  T_BackendEffectiveContext,
  T_BackendGradedOffer,
  T_BackendHistoryTurn,
  T_GradeKey,
  T_LimitKey,
  T_OfferAssistantChatResponse,
  T_OfferAssistantSession,
  T_PaymentFrequency,
  T_RejectedVariantsData,
  T_VariantsData,
} from "dtos/offer-assistant.dto";

// ─── Types ────────────────────────────────────────────────────────────────────

export type T_StripScale = { weeks: number; amount: number };

export type T_Level = "neutral" | "good" | "warn" | "bad";

export type T_SkeletonKind = "cards" | "table" | "compare" | "lines";

export type T_SeedItem =
  | { role: "user"; text: string }
  | { role: "assistant"; turn: T_AssistantTurn }
  /** A restored `action: "error"` turn — shown as a notice, without Retry. */
  | { role: "error"; message: string };

// ─── Constants ────────────────────────────────────────────────────────────────

/** Length of one payment period in weeks (daily = business day). */
export const PERIOD_WEEKS: Record<T_PaymentFrequency, number> = { daily: 0.2, weekly: 1, monthly: 4.345 };
export const UNIT: Record<T_PaymentFrequency, string> = { daily: "day", weekly: "week", monthly: "month" };
const UNIT_SHORT: Record<T_PaymentFrequency, string> = { daily: "day", weekly: "wk", monthly: "mo" };

export const GRADE_ORDER: T_GradeKey[] = ["economic_worth", "client_utility", "creditor_acceptance"];

export const LEVEL_TEXT_CLASS: Record<T_Level, string> = {
  neutral: "!text-gray-900",
  good: "!text-green-700",
  warn: "!text-amber-700",
  bad: "!text-red-700",
};

export const LEVEL_METER_CLASS: Record<T_Level, string> = {
  neutral: "bg-violet-500",
  good: "bg-green-500",
  warn: "bg-amber-400",
  bad: "bg-red-400",
};

const SORT_MAP: Record<string, T_GradeKey> = {
  affordability: "client_utility",
  value: "economic_worth",
  acceptance: "creditor_acceptance",
};

// ─── Formatting ───────────────────────────────────────────────────────────────

export const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

export const kMoney = (n: number) =>
  n >= 1000 ? `$${(Math.round(n / 100) / 10).toString().replace(/\.0$/, "")}k` : `$${Math.round(n)}`;

export const SCORE_MAX = 10;
const SCORE_FROM_UNIT = true;
export const toScore = (raw: number) => (SCORE_FROM_UNIT ? raw * SCORE_MAX : raw);
/** Fraction of the scale — drives meter widths. */
export const scoreFraction = (raw: number) => toScore(raw) / SCORE_MAX;

export const score = (n: number) => toScore(n).toFixed(1);

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "2 wk pause, $12,000 now, then $850/week × 20" */
export function shapeText(v: T_AssistantOfferVariables): string {
  const parts: string[] = [];
  if (v.deferral_weeks) parts.push(`${v.deferral_weeks} wk pause`);
  const run = `${money(v.periodic_amount)}/${UNIT[v.payment_frequency]} × ${v.total_period}`;
  if (v.upfront_amount && v.total_period) parts.push(`${money(v.upfront_amount)} now`, `then ${run}`);
  else if (v.upfront_amount) parts.push(`${parts.length ? "one" : "One"} payment of ${money(v.upfront_amount)}`);
  else parts.push(run);
  return parts.join(", ");
}

/** "$8k + $1.2k/wk" */
export const shortShape = (v: T_AssistantOfferVariables) =>
  (v.upfront_amount ? `${kMoney(v.upfront_amount)} + ` : "") +
  `${kMoney(v.periodic_amount)}/${UNIT_SHORT[v.payment_frequency]}`;

/** "$12k now + $850/wk × 20" */
export const mentionShape = (v: T_AssistantOfferVariables) =>
  (v.deferral_weeks ? `${v.deferral_weeks} wk pause, ` : "") +
  (v.upfront_amount ? `${kMoney(v.upfront_amount)} now + ` : "") +
  `${kMoney(v.periodic_amount)}/${UNIT_SHORT[v.payment_frequency]} × ${v.total_period}`;

const horizonWeeks = (v: T_AssistantOfferVariables) =>
  (v.deferral_weeks || 0) + (v.upfront_amount ? 1 : 0) + v.total_period * PERIOD_WEEKS[v.payment_frequency];

/** Shared time axis + amount scale for every strip inside one block. */
export function stripScale(offers: T_AssistantOffer[]): T_StripScale {
  const weeks = Math.max(1, ...offers.map((o) => horizonWeeks(o.variables))) * 1.03;
  const amount = Math.max(1, ...offers.map((o) => o.variables.upfront_amount || o.variables.periodic_amount));
  return { weeks, amount };
}

// ─── Grades ───────────────────────────────────────────────────────────────────

export const affordLevel = (x: number): T_Level => (toScore(x) >= 6 ? "good" : toScore(x) >= 3.5 ? "warn" : "bad");
/** Raw 0–1: green ≥ 0.80, amber 0.60–0.79, red < 0.60. */
const acceptanceLevel = (x: number): T_Level => (toScore(x) >= 8 ? "good" : toScore(x) >= 6 ? "warn" : "bad");
export const gradeLevel = (k: T_GradeKey, v: number): T_Level =>
  k === "client_utility" ? affordLevel(v) : k === "creditor_acceptance" ? acceptanceLevel(v) : "neutral";

/** Every grade is higher-is-better, so there's no per-grade direction to track. */
export const GRADE_META: Record<T_GradeKey, { label: string; tooltip: string; format: (n: number) => string }> = {
  economic_worth: {
    label: "Value to creditor",
    tooltip:
      "Present value of everything the creditor receives — upfront plus the discounted payments. Never exceeds the amount owed.",
    format: money,
  },
  client_utility: {
    label: "Affordability",
    tooltip: "How sustainable this is for the client given their cash on hand and weekly cashflow. 0 to 10.",
    format: score,
  },
  creditor_acceptance: {
    label: "Creditor acceptance",
    tooltip:
      "How likely the creditor is to accept this offer, compared with what the original contract would pay over the same period. 10 means fully acceptable; 0 means a likely rejection.",
    format: score,
  },
};

export const heldText = (anchor: T_GradeKey, value: number) =>
  `${GRADE_META[anchor].label} held at ${GRADE_META[anchor].format(value)}`;

/** Highest value on a grade — every grade is higher-is-better. */
export const bestBy = (offers: T_AssistantOffer[], k: T_GradeKey) =>
  offers.reduce((best, o) => (o.grade[k] > best.grade[k] ? o : best), offers[0]);

/** Display-only badges derived from grades within one block. Max one per card. */
export function deriveBadges(offers: T_AssistantOffer[]): Record<string, string> {
  const out: Record<string, string> = {};
  if (offers.length < 2) return out;
  const labels: [T_GradeKey, string][] = [
    ["economic_worth", "Highest value"],
    ["client_utility", "Most affordable"],
    ["creditor_acceptance", "Easiest to accept"],
  ];
  for (const [k, label] of labels) {
    const o = bestBy(offers, k);
    if (!out[o.ref]) out[o.ref] = label;
  }
  return out;
}

// ─── Refs ─────────────────────────────────────────────────────────────────────

export const variantLabel = (ref: string) => ref.split(" · ")[1] ?? ref;
export const variantNo = (ref: string) => Number(/Variant (\d+)/.exec(ref)?.[1] ?? 0);
export const refNo = (ref: string) => Number(/Offer (\d+)/.exec(ref)?.[1] ?? 0);

export const joinRefs = (refs: string[]) =>
  refs.length <= 1 ? refs.join("") : `${refs.slice(0, -1).join(", ")} and ${refs[refs.length - 1]}`;

// ─── Turn helpers ─────────────────────────────────────────────────────────────

export const isRejected = (d: T_VariantsData | T_RejectedVariantsData): d is T_RejectedVariantsData =>
  (d as T_RejectedVariantsData).rejected === true;

export const isContextAction = (action: string | null) => action === "get_context" || action === "update_context";

/** Which skeleton shape to show while waiting for a reply. */
export function guessSkeleton(text: string): T_SkeletonKind {
  if (/variant/i.test(text)) return "table";
  if (/^compare/i.test(text)) return "compare";
  if (/generat|new offers|top|all offers/i.test(text)) return "cards";
  return "lines";
}

export function offersInTurn(turn: T_AssistantTurn): T_AssistantOffer[] {
  switch (turn.action) {
    case "generate_offers":
    case "show_all_offers":
    case "show_top_offers":
    case "get_offer":
    case "compare_offers":
      return turn.data;
    case "generate_variants":
    case "show_variants":
      return isRejected(turn.data) ? [] : [turn.data.source, ...turn.data.variants];
    default:
      return [];
  }
}

// ─── Backend → UI adapters ────────────────────────────────────────────────────

function toOffer(g: T_BackendGradedOffer, kindOverride?: T_AssistantOfferKind): T_AssistantOffer {
  return {
    ref: g.ref,
    kind: kindOverride ?? g.kind,
    parent_ref: g.parent_offer_ref ?? undefined,
    variables: { ...g.variables },
    grade: {
      economic_worth: g.grade.economic_worth,
      client_utility: g.grade.client_utility,
      creditor_acceptance: g.grade.creditor_acceptance,
      nominal_total: g.grade.nominal_total,
      affordability_breakdown: {
        cash_vs_upfront: g.grade.upfront_utility_score,
        cashflow_vs_payment: g.grade.periodic_utility_score,
        deferral_room: g.grade.deferral_utility_score,
      },
    },
    summary: g.summary,
    recommended: g.recommended || undefined,
    balanced: g.balanced || undefined,
    held: g.anchor && g.anchor_target != null ? { anchor: g.anchor, value: g.anchor_target } : undefined,
  };
}

function toContextData(ctx: T_BackendEffectiveContext, prev: T_BackendEffectiveContext | null): T_AssistantContextData {
  const limits: T_AssistantLimit[] = [];
  const push = (key: T_LimitKey, value: number, def: number, custom: boolean) =>
    limits.push({ key, value, default_value: def, source: custom ? "custom" : "default" });

  push("max_upfront", ctx.max_upfront ?? ctx.derived_upfront_cap, ctx.derived_upfront_cap, ctx.max_upfront != null);
  push(
    "max_periodic",
    ctx.max_periodic ?? ctx.derived_periodic_weekly_cap,
    ctx.derived_periodic_weekly_cap,
    ctx.max_periodic != null,
  );
  if (ctx.max_total_weeks != null) push("max_term_weeks", ctx.max_total_weeks, ctx.max_total_weeks, true);
  push(
    "max_deferral_weeks",
    ctx.effective_deferral_cap,
    ctx.max_deferral_weeks == null ? ctx.effective_deferral_cap : 6,
    ctx.max_deferral_weeks != null,
  );

  const changed: T_LimitKey[] = [];
  if (prev) {
    if ((prev.max_upfront ?? prev.derived_upfront_cap) !== (ctx.max_upfront ?? ctx.derived_upfront_cap))
      changed.push("max_upfront");
    if (
      (prev.max_periodic ?? prev.derived_periodic_weekly_cap) !== (ctx.max_periodic ?? ctx.derived_periodic_weekly_cap)
    )
      changed.push("max_periodic");
    if (prev.max_total_weeks !== ctx.max_total_weeks) changed.push("max_term_weeks");
    if (prev.effective_deferral_cap !== ctx.effective_deferral_cap) changed.push("max_deferral_weeks");
  }
  return { limits, changed };
}

const pickRecommended = (offers: T_AssistantOffer[]) => offers.find((o) => o.recommended)?.ref;

const reasonToRequestedValue = (reason: string) => {
  const m = /([\d.]+)/.exec(reason ?? "");
  return m ? Number(m[1]) : 0;
};

const EMPTY_SOURCE = (ref: string): T_AssistantOffer => ({
  ref,
  kind: "base",
  variables: { upfront_amount: 0, periodic_amount: 0, payment_frequency: "weekly", total_period: 0, deferral_weeks: 0 },
  grade: {
    economic_worth: 0,
    client_utility: 0,
    creditor_acceptance: 0,
    nominal_total: 0,
    affordability_breakdown: { cash_vs_upfront: 0, cashflow_vs_payment: 0, deferral_room: 0 },
  },
});

const plainMessage = (message: string, suggestions: T_AssistantTurn["suggestions"] = []): T_AssistantTurn => ({
  action: "out_of_scope",
  message,
  data: null,
  suggestions,
  meta: {},
});

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);

/** Meta fields that can appear on any action — copied onto every turn after adapting. */
function sharedMeta(meta: Record<string, unknown>): T_AssistantTurnMeta {
  const breaches = Array.isArray(meta.breaches_due_amount) ? (meta.breaches_due_amount as string[]) : [];
  return {
    breaches_due_amount: breaches.length ? breaches : undefined,
    due_amount: typeof meta.due_amount === "number" ? meta.due_amount : undefined,
    no_recommendation_reason: str(meta.no_recommendation_reason),
    truncated: str(meta.truncated),
    partial: str(meta.partial),
    restored_note: str(meta.restored_note),
  };
}

/** An over-the-limit offer must never show ★ Recommended — enforced once here, for every block. */
function stripOverLimit(turn: T_AssistantTurn): T_AssistantTurn {
  const over = new Set(turn.meta.breaches_due_amount ?? []);
  if (!over.size) return turn;
  const clean = (o: T_AssistantOffer): T_AssistantOffer => (over.has(o.ref) ? { ...o, recommended: undefined } : o);
  const meta =
    turn.meta.recommended_ref && over.has(turn.meta.recommended_ref)
      ? { ...turn.meta, recommended_ref: undefined }
      : turn.meta;

  switch (turn.action) {
    case "generate_offers":
    case "show_all_offers":
    case "show_top_offers":
    case "get_offer":
    case "grade_offer":
    case "compare_offers":
      return { ...turn, meta, data: turn.data.map(clean) };
    case "generate_variants":
    case "show_variants":
      return isRejected(turn.data)
        ? { ...turn, meta }
        : {
            ...turn,
            meta,
            data: { ...turn.data, source: clean(turn.data.source), variants: turn.data.variants.map(clean) },
          };
    default:
      return { ...turn, meta };
  }
}

/**
 * Convert a backend chat envelope into the UI's T_AssistantTurn union. Used for live and restored turns alike.
 * `prevContext` is the last EffectiveContext seen *before* this envelope — used for the "Updated N limits" diff.
 * Throws on `action: "error"` so the caller renders the error state.
 */
export function envelopeToTurn(
  env: T_OfferAssistantChatResponse,
  prevContext: T_BackendEffectiveContext | null,
): T_AssistantTurn {
  const turn = adaptEnvelope(env, prevContext);
  return stripOverLimit({ ...turn, meta: { ...turn.meta, ...sharedMeta(env.meta ?? {}) } } as T_AssistantTurn);
}

function adaptEnvelope(
  env: T_OfferAssistantChatResponse,
  prevContext: T_BackendEffectiveContext | null,
): T_AssistantTurn {
  const suggestions = env.suggestions ?? [];
  const meta = env.meta ?? {};

  // Backend can attach meta.error to any action — render it as a plain message with chips.
  if (typeof meta.error === "string") return plainMessage(meta.error, suggestions);

  switch (env.action) {
    case "generate_offers":
    case "show_all_offers":
    case "show_top_offers":
    case "get_offer":
    case "compare_offers": {
      const offers = (env.data as T_BackendGradedOffer[]).map((g) => toOffer(g));
      const turnMeta: T_AssistantTurnMeta = {};
      if (env.action === "generate_offers") {
        turnMeta.limits_state = env.session.custom_limits_count > 0 ? "custom" : "default";
        turnMeta.recommended_ref = pickRecommended(offers);
      } else if (env.action === "show_top_offers") {
        const sorted = typeof meta.sorted_by === "string" ? meta.sorted_by : "affordability";
        turnMeta.sorted_by = SORT_MAP[sorted] ?? "client_utility";
      } else if (env.action === "compare_offers") {
        // null = nothing qualifies. Never recompute a winner client-side.
        turnMeta.recommended_ref = str(meta.recommended_ref);
        const missing = meta.missing as string[] | undefined;
        if (missing?.length) turnMeta.note = `Not found: ${missing.join(", ")}`;
      }
      return { action: env.action, message: env.message, data: offers, suggestions, meta: turnMeta };
    }

    case "grade_offer": {
      // 1–6 offers, refs "What-if 1…N". Backend sends kind "base"; the UI kind keeps them out of
      // @-mentions, selection and caching. ★ comes only from meta.recommended_ref (2+ offers).
      const whatIfs = (env.data as T_BackendGradedOffer[]).map((g) => ({
        ...toOffer(g, "whatif"),
        recommended: undefined,
      }));
      const turnMeta: T_AssistantTurnMeta = {};
      if (typeof meta.note === "string") turnMeta.note = meta.note;
      if (whatIfs.length > 1) turnMeta.recommended_ref = str(meta.recommended_ref);
      return { action: "grade_offer", message: env.message, data: whatIfs, suggestions, meta: turnMeta };
    }

    case "generate_variants":
    case "show_variants": {
      const parent = meta.parent_offer as T_BackendGradedOffer | undefined;
      const parentRef = (meta.parent_offer_ref as string | undefined) ?? parent?.ref ?? "";
      // Backend defaults variants to affordability when no anchor is given.
      const anchor = (meta.anchor as T_GradeKey | undefined) ?? "client_utility";
      const target = (meta.target as number | undefined) ?? (meta.anchor_target as number | undefined);

      if (meta.status === "rejected") {
        const reason = (meta.message as string | undefined) ?? env.message;
        const rejected: T_RejectedVariantsData = {
          rejected: true,
          source_ref: parentRef,
          anchor,
          requested_value:
            typeof meta.target_requested === "number" ? meta.target_requested : reasonToRequestedValue(reason),
          reason,
          reachable_value: (meta.achievable_bound as number | undefined) ?? 0,
        };
        return { action: env.action, message: env.message, data: rejected, suggestions, meta: {} };
      }

      const variants = (env.data as T_BackendGradedOffer[]).map((g) => toOffer(g));
      const source = parent ? toOffer(parent) : EMPTY_SOURCE(parentRef);
      const data: T_VariantsData = {
        source,
        anchor,
        anchor_value: target ?? source.grade[anchor],
        variants,
        requested: (meta.requested_count as number | undefined) ?? variants.length,
      };
      const turnMeta: T_AssistantTurnMeta = {
        recommended_ref: (meta.recommended_ref as string | undefined) ?? pickRecommended(variants),
        balanced_ref: (meta.balanced_ref as string | undefined) ?? variants.find((v) => v.balanced)?.ref,
      };
      if (typeof meta.note === "string") turnMeta.note = meta.note;
      return { action: env.action, message: env.message, data, suggestions, meta: turnMeta };
    }

    case "get_context":
    case "update_context": {
      const ctx = (env.data as T_BackendEffectiveContext[])[0];
      const data = toContextData(ctx, env.action === "update_context" ? prevContext : null);
      return { action: env.action, message: env.message, data, suggestions, meta: {} };
    }

    case "clarify":
      return { action: "clarify", message: env.message, data: null, suggestions, meta: {} };

    case "out_of_scope":
      return plainMessage(typeof meta.message === "string" ? meta.message : env.message, suggestions);

    case "error":
      throw new Error(env.message || "Couldn't reach the offer engine.");

    default:
      return plainMessage(env.message, suggestions);
  }
}

/**
 * Restored turns share the live envelope shape, so they go through the same `envelopeToTurn`.
 * Walked in order so each update_context diffs against the limits before it, exactly as live.
 */
export function historyToSeeds(
  turns: T_BackendHistoryTurn[],
  session: T_OfferAssistantSession,
): { seeds: T_SeedItem[]; lastContext: T_BackendEffectiveContext | null } {
  const seeds: T_SeedItem[] = [];
  let context: T_BackendEffectiveContext | null = null;

  for (const t of turns) {
    if (t.role === "user") {
      seeds.push({ role: "user", text: t.message });
      continue;
    }
    const env: T_OfferAssistantChatResponse = {
      chat_id: "",
      message: t.message,
      action: t.action,
      data: t.data,
      meta: t.meta ?? {},
      suggestions: t.suggestions ?? [],
      session,
    };
    try {
      seeds.push({ role: "assistant", turn: envelopeToTurn(env, context) });
    } catch {
      seeds.push({ role: "error", message: str(t.meta?.error) ?? t.message });
    }
    if (isContextAction(t.action) && t.data[0]) context = t.data[0] as T_BackendEffectiveContext;
  }
  return { seeds, lastContext: context };
}
